import { useCallback, useEffect, useState } from "react";

declare global {
  interface Window {
    __BUUKD_SW_REGISTRATION__?: ServiceWorkerRegistration;
    __BUUKD_SW_RELOAD__?: (reloadPage?: boolean) => Promise<void>;
  }
}

/**
 * Hook for PWA "Check for updates": triggers a service worker update check and,
 * when a new version is found, applies it by reloading the page.
 * Only does anything in production when the app is installed as a PWA (SW registered).
 */
export function usePwaUpdate() {
  const [checking, setChecking] = useState(false);

  const checkForUpdates = useCallback(async () => {
    if (!("serviceWorker" in navigator) || import.meta.env.DEV) return;
    const reg =
      window.__BUUKD_SW_REGISTRATION__ ??
      (await navigator.serviceWorker.getRegistration());
    if (!reg) return;
    setChecking(true);
    try {
      await reg.update();
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator) || import.meta.env.DEV) return;

    const handler = () => {
      const reload = window.__BUUKD_SW_RELOAD__;
      if (reload) reload(true);
    };

    window.addEventListener("pwa-need-refresh", handler);
    return () => window.removeEventListener("pwa-need-refresh", handler);
  }, []);

  const isSupported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    import.meta.env.PROD;

  return { checkForUpdates, checking, isSupported };
}
